import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { usePatchMembershipType } from 'src/features/membershipType/api/usePatchMembershipType';
import { RestrictedGateway } from 'src/features/membershipType/types';
import { LIST_RESTRICTED_GATEWAYS_RESOURCE } from 'src/features/membershipType/api/useListRestrictedGateways';
import { restrictedGatewayMessages } from 'src/features/membershipType/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { MEMBERSHIP_TYPE_RESOURCE } from 'src/features/membershipType/api/useGetMembershipType';
import { MEMBERSHIP_TYPE_CONFIGURATION_RESOURCE } from 'src/features/membershipType/api/useGetMembershipTypeConfiguration';

interface DeleteRestrictedGatewayProps<TData> extends DataTableNewEntryProps<TData> {
    selectedRestrictedGateway?: RestrictedGateway;
    restrictedGateways?: string[];
    children?: React.ReactNode;
}

function DeleteRestrictedGateway<TData>({
    selectedRestrictedGateway,
    restrictedGateways,
    children,
}: DeleteRestrictedGatewayProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage, locale } = useIntl();
    const { membershipTypeId = '' } = useParams();
    const { parkingId } = useParking();

    const remainingRestrictedGateways = React.useMemo(() => {
        return restrictedGateways?.filter((gatewayId) => gatewayId !== selectedRestrictedGateway?.id) ?? [];
    }, [restrictedGateways, selectedRestrictedGateway]);

    const { mutate: deleteRestrictedGateway } = usePatchMembershipType({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_RESTRICTED_GATEWAYS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_CONFIGURATION_RESOURCE] });

            setIsModalOpen(false);
            toast({
                title: formatMessage(restrictedGatewayMessages.deleteRestrictedGatewaySuccess),
                description: formatMessage(restrictedGatewayMessages.deleteRestrictedGatewaySuccessDescription, {
                    entry: selectedRestrictedGateway?.translations?.find((t) => t.language === locale)?.name ?? '',
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    if (!selectedRestrictedGateway) return null;

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...restrictedGatewayMessages.deleteRestrictedGateway },
                subTitle: {
                    ...restrictedGatewayMessages.deleteRestrictedGatewaySubtitle,
                    values: {
                        entry: selectedRestrictedGateway?.translations?.find((t) => t.language === locale)?.name ?? '',
                    },
                },
                icon: ICON_MAP.DELETE as IconProp,
                confirmationMessage: {
                    ...restrictedGatewayMessages.confirmDeleteRestrictedGateway,
                    values: {
                        entry: selectedRestrictedGateway?.translations?.find((t) => t.language === locale)?.name ?? '',
                    },
                },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: generalMessages.delete,
                callbackFn: () => {
                    if (selectedRestrictedGateway && restrictedGateways) {
                        return deleteRestrictedGateway({
                            parkingId,
                            id: membershipTypeId || '',
                            restricted_gateways: remainingRestrictedGateways,
                        });
                    }
                },
            }}
        >
            {children}
        </ConfirmationModal>
    );
}

export default DeleteRestrictedGateway;
